/**
 * UI-only state for the Maintenance panels: which detail tab is open, which
 * reading the Readings tab shows, which dialog is open, which spec sheet the
 * Parts tab should open, the card tree expansion and the search box. Scene and
 * domain state stays in useTwin; selection stays in useUI.
 */
import { create } from 'zustand';

export type TwinDialog = 'report' | 'health' | 'history';

export interface ReadingRef {
  subsystemId: string;
  dataId: string;
}

interface TwinUiState {
  tab: string;
  reading: ReadingRef | null;
  dialog: TwinDialog | null;
  /** Equipment instance the Parts tab opens (set by the report's spec-sheet action). */
  partsInstanceId: string | null;
  expanded: Record<string, boolean>;
  search: string;
  setTab: (key: string) => void;
  setReading: (ref: ReadingRef | null) => void;
  openDialog: (dialog: TwinDialog) => void;
  closeDialog: () => void;
  setPartsInstance: (id: string | null) => void;
  toggleExpanded: (id: string) => void;
  setExpanded: (ids: string[], open: boolean) => void;
  setSearch: (q: string) => void;
}

export const useTwinUi = create<TwinUiState>()((set) => ({
  tab: 'health',
  reading: null,
  dialog: null,
  partsInstanceId: null,
  expanded: {},
  search: '',
  setTab: (tab) => {
    set({ tab });
    focusDetailTab(tab);
  },
  setReading: (reading) => set({ reading }),
  openDialog: (dialog) => set({ dialog }),
  closeDialog: () => set({ dialog: null }),
  setPartsInstance: (partsInstanceId) => set({ partsInstanceId }),
  toggleExpanded: (id) => set((s) => ({ expanded: { ...s.expanded, [id]: !s.expanded[id] } })),
  setExpanded: (ids, open) =>
    set((s) => {
      const expanded = { ...s.expanded };
      for (const id of ids) expanded[id] = open;
      return { expanded };
    }),
  setSearch: (search) => set({ search }),
}));

/**
 * The detail panel's Tabs.Root is owned by the shell. When it is bound to
 * useTwinUi.tab this is a no-op; until then it clicks the matching radix
 * trigger (radix ids end in "-trigger-<value>") so cross-tab jumps still work.
 */
function focusDetailTab(key: string): void {
  if (typeof document === 'undefined') return;
  const trigger = document.querySelector<HTMLButtonElement>(`[role="tab"][id$="-trigger-${key}"]`);
  if (trigger && trigger.getAttribute('data-state') !== 'active') trigger.click();
}
